import React, { ChangeEvent, useMemo, useRef, useState } from 'react';

import { parse, unparse } from 'papaparse';

import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import { BEDBANK_PROPERTY_DOMAINS_ENUM, BEDBANK_PROPERTY_STATUSES_ENUM } from '~/consts/bedbank';

import bedbankService, {
  BulkPatchPropertyCommitRow,
  BulkPatchPropertyInput,
  BulkPatchPropertyPreviewRow,
  BulkPatchPropertyValueChange,
} from '~/services/BedbankService';

import { downloadCsvFile } from '~/utils/csvDownload';

import BulkPropertyUpdateCommitTable from './BulkPropertyUpdateCommitTable';
import BulkPropertyUpdatePreviewTable from './BulkPropertyUpdatePreviewTable';

const CSV_HEADERS = ['id', 'status', 'domain', 'airport'] as const;
const CSV_TEMPLATE = `${CSV_HEADERS.join(',')}\n`;
const PREVIEW_EXPORT_HEADERS = ['Property ID', 'Action', 'Status', 'Domain', 'Airport', 'Error'];
const COMMIT_EXPORT_HEADERS = ['Property ID', 'Status', 'Domain', 'Airport'];

type CsvHeader = (typeof CSV_HEADERS)[number];
type CsvRow = Partial<Record<CsvHeader, string>>;
type TabValue = 'input' | 'preview' | 'commit';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface ParseResult {
  errors: Array<string>;
  rows: Array<BulkPatchPropertyInput>;
}

function normaliseHeader(header: string) {
  return header.trim();
}

function parseOptionalCsvValue(value?: string): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function parseNullableCsvValue(value?: string): string | null | undefined {
  const trimmed = value?.trim();
  if (!trimmed) {
    return undefined;
  }

  return trimmed.toLowerCase() === 'null' ? null : trimmed;
}

function isPropertyStatus(value: string): value is NonNullable<BulkPatchPropertyInput['status']> {
  return BEDBANK_PROPERTY_STATUSES_ENUM.includes(value);
}

function isPropertyDomain(value: string): value is NonNullable<BulkPatchPropertyInput['domain']> {
  return BEDBANK_PROPERTY_DOMAINS_ENUM.includes(value);
}

function parseCsv(csvText: string): ParseResult {
  const cleanedCsvText = csvText.replace(/^\uFEFF/, '');
  const parsed = parse<CsvRow>(cleanedCsvText, {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: normaliseHeader,
    transform: (value) => value.trim(),
  });
  const fields = parsed.meta.fields ?? [];
  const errors = parsed.errors.map(
    (error) => `Row ${typeof error.row === 'number' ? error.row + 2 : 1}: ${error.message}`,
  );

  if (fields.join(',') !== CSV_HEADERS.join(',')) {
    return {
      errors: [`CSV header must be exactly: ${CSV_HEADERS.join(',')}`],
      rows: [],
    };
  }

  const rows: Array<BulkPatchPropertyInput> = [];
  parsed.data.forEach((row, index) => {
    const rowNumber = index + 2;
    const id = row.id?.trim();
    const status = parseOptionalCsvValue(row.status);
    const domain = parseOptionalCsvValue(row.domain);
    const airport = parseNullableCsvValue(row.airport);
    const parsedStatus = status && isPropertyStatus(status) ? status : undefined;
    const parsedDomain = domain && isPropertyDomain(domain) ? domain : undefined;

    if (!id) {
      errors.push(`Row ${rowNumber}: id is required`);
      return;
    }
    if (status && !parsedStatus) {
      errors.push(`Row ${rowNumber}: status "${status}" is not valid`);
      return;
    }
    if (domain && !parsedDomain) {
      errors.push(`Row ${rowNumber}: domain "${domain}" is not valid`);
      return;
    }
    if (!status && !domain && typeof airport === 'undefined') {
      errors.push(`Row ${rowNumber}: at least one of status, domain, or airport is required`);
      return;
    }

    rows.push({
      id,
      ...(parsedStatus ? { status: parsedStatus } : {}),
      ...(parsedDomain ? { domain: parsedDomain } : {}),
      ...(typeof airport !== 'undefined' ? { airport } : {}),
    });
  });

  return { errors, rows };
}

function getRowKey(row: Pick<BulkPatchPropertyPreviewRow, 'id'>) {
  return row.id;
}

function sortPreviewRows(rows: Array<BulkPatchPropertyPreviewRow>) {
  const actionOrder: Record<BulkPatchPropertyPreviewRow['action'], number> = {
    error: 0,
    update: 1,
    'no-change': 2,
  };

  return [...rows].sort((rowA, rowB) => {
    const actionDiff = actionOrder[rowA.action] - actionOrder[rowB.action];
    if (actionDiff !== 0) {
      return actionDiff;
    }

    return rowA.id.localeCompare(rowB.id);
  });
}

function formatExportValue(value: string | null | undefined) {
  if (typeof value === 'undefined') {
    return '';
  }

  return value ?? 'null';
}

function formatPreviewChangeForExport(change?: BulkPatchPropertyValueChange) {
  if (!change) {
    return '';
  }

  const oldValue = formatExportValue(change.oldValue);
  const newValue = formatExportValue(change.newValue);
  return oldValue === newValue ? oldValue : `${oldValue} ⇒ ${newValue}`;
}

function buildPreviewExportRows(rows: Array<BulkPatchPropertyPreviewRow>): Array<Record<string, unknown>> {
  return rows.map((row) => ({
    'Property ID': row.id,
    Action: row.action,
    Status: formatPreviewChangeForExport(row.status),
    Domain: formatPreviewChangeForExport(row.domain),
    Airport: formatPreviewChangeForExport(row.airport),
    Error: row.error ?? '',
  }));
}

function buildCommitExportRows(rows: Array<BulkPatchPropertyCommitRow>): Array<Record<string, unknown>> {
  return rows.map((row) => ({
    'Property ID': row.id,
    Status: formatExportValue(row.status),
    Domain: formatExportValue(row.domain),
    Airport: formatExportValue(row.airport),
  }));
}

function buildReverseCsvRows(
  committedRows: Array<BulkPatchPropertyCommitRow>,
  previewRows: Array<BulkPatchPropertyPreviewRow>,
): Array<Record<CsvHeader, string>> {
  const previewRowsById = new Map(previewRows.map((row) => [row.id, row]));

  return committedRows.map((committedRow) => {
    const previewRow = previewRowsById.get(committedRow.id);
    return {
      id: committedRow.id,
      status: typeof committedRow.status !== 'undefined' ? formatExportValue(previewRow?.status?.oldValue) : '',
      domain: typeof committedRow.domain !== 'undefined' ? formatExportValue(previewRow?.domain?.oldValue) : '',
      airport: typeof committedRow.airport !== 'undefined' ? formatExportValue(previewRow?.airport?.oldValue) : '',
    };
  });
}

function getExportTimestamp() {
  return new Date()
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}Z$/, 'Z');
}

export default function BulkPropertyUpdateDialog({ open, onClose }: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [activeTab, setActiveTab] = useState<TabValue>('input');
  const [csvText, setCsvText] = useState(CSV_TEMPLATE);
  const [previewRows, setPreviewRows] = useState<Array<BulkPatchPropertyPreviewRow>>([]);
  const [commitRows, setCommitRows] = useState<Array<BulkPatchPropertyCommitRow>>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(new Set());
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const parsedRows = useMemo(() => parseCsv(csvText), [csvText]);
  const sortedPreviewRows = useMemo(() => sortPreviewRows(previewRows), [previewRows]);
  const selectablePreviewRows = useMemo(() => previewRows.filter((row) => row.action === 'update'), [previewRows]);
  const selectedRows = useMemo(
    () => parsedRows.rows.filter((row) => selectedRowKeys.has(row.id)),
    [parsedRows.rows, selectedRowKeys],
  );
  const canPreview = parsedRows.rows.length > 0 && parsedRows.errors.length === 0 && !isPreviewing;
  const canCommit = selectedRows.length > 0 && !isCommitting;

  const reset = () => {
    setActiveTab('input');
    setCsvText(CSV_TEMPLATE);
    setPreviewRows([]);
    setCommitRows([]);
    setSelectedRowKeys(new Set());
    setApiError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDialogClose: DialogProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick') {
      return;
    }

    handleClose();
  };

  const handleCsvFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      setCsvText(text);
      setPreviewRows([]);
      setCommitRows([]);
      setSelectedRowKeys(new Set());
      setApiError(null);
      setActiveTab('input');
    };
    reader.readAsText(file);
    event.currentTarget.value = '';
  };

  const handlePreview = async () => {
    setApiError(null);
    setPreviewRows([]);
    setCommitRows([]);
    setSelectedRowKeys(new Set());

    if (parsedRows.errors.length > 0) {
      setActiveTab('input');
      return;
    }

    setIsPreviewing(true);
    try {
      const response = await bedbankService.bulkPatchProperties(parsedRows.rows, 'preview');
      const rows = response.result.preview ?? [];
      setPreviewRows(rows);
      setSelectedRowKeys(new Set(rows.filter((row) => row.action === 'update').map(getRowKey)));
      setActiveTab('preview');
    } catch (error) {
      setApiError(error instanceof Error ? error.message : 'Failed to preview bulk update');
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleCommit = async () => {
    setApiError(null);
    setIsCommitting(true);
    try {
      const response = await bedbankService.bulkPatchProperties(selectedRows, 'commit');
      setCommitRows(response.result.commit ?? []);
      setActiveTab('commit');
    } catch (error) {
      setApiError(error instanceof Error ? error.message : 'Failed to commit bulk update');
    } finally {
      setIsCommitting(false);
    }
  };

  const toggleRow = (row: BulkPatchPropertyPreviewRow) => {
    if (row.action !== 'update') {
      return;
    }

    setSelectedRowKeys((current) => {
      const next = new Set(current);
      const key = getRowKey(row);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleAllSelectableRows = () => {
    setSelectedRowKeys((current) => {
      if (current.size === selectablePreviewRows.length) {
        return new Set();
      }

      return new Set(selectablePreviewRows.map(getRowKey));
    });
  };

  const handlePreviewExport = () => {
    const timestamp = getExportTimestamp();
    downloadCsvFile(
      buildPreviewExportRows(sortedPreviewRows),
      PREVIEW_EXPORT_HEADERS,
      `bedbank-property-bulk-update-preview-${timestamp}.csv`,
    );
  };

  const handleCommitExport = () => {
    const timestamp = getExportTimestamp();
    downloadCsvFile(
      buildCommitExportRows(commitRows),
      COMMIT_EXPORT_HEADERS,
      `bedbank-property-bulk-update-commit-${timestamp}.csv`,
    );
  };

  const handleReverse = () => {
    const reverseRows = buildReverseCsvRows(commitRows, previewRows);
    setCsvText(unparse(reverseRows, { columns: [...CSV_HEADERS] }));
    setPreviewRows([]);
    setCommitRows([]);
    setSelectedRowKeys(new Set());
    setApiError(null);
    setActiveTab('input');
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={handleDialogClose}>
      <DialogTitle>Bulk Update Bedbank Properties</DialogTitle>
      <DialogContent>
        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} sx={{ mb: 2 }}>
          <Tab label="Input" value="input" />
          <Tab label={`Preview${previewRows.length ? ` (${previewRows.length})` : ''}`} value="preview" />
          <Tab label={`Commit${commitRows.length ? ` (${commitRows.length})` : ''}`} value="commit" />
        </Tabs>

        {apiError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {apiError}
          </Alert>
        )}

        {activeTab === 'input' && (
          <Stack spacing={2}>
            <Alert severity="info">CSV header must be exactly: {CSV_HEADERS.join(',')}</Alert>
            {parsedRows.errors.length > 0 && (
              <Alert severity="error">
                <Typography fontWeight={600}>CSV issues</Typography>
                <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
                  {parsedRows.errors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </Box>
              </Alert>
            )}
            <TextField
              label="Property update CSV"
              value={csvText}
              onChange={(event) => {
                setCsvText(event.target.value);
                setPreviewRows([]);
                setCommitRows([]);
                setSelectedRowKeys(new Set());
              }}
              multiline
              minRows={12}
              fullWidth
              inputProps={{ spellCheck: false }}
            />
          </Stack>
        )}

        {activeTab === 'preview' && (
          <BulkPropertyUpdatePreviewTable
            rows={sortedPreviewRows}
            selectedRowKeys={selectedRowKeys}
            onToggleRow={toggleRow}
            onToggleAllRows={toggleAllSelectableRows}
          />
        )}

        {activeTab === 'commit' && <BulkPropertyUpdateCommitTable rows={commitRows} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        {activeTab === 'input' && (
          <>
            <Button component="label" startIcon={<UploadFileIcon />} variant="outlined">
              Import CSV
              <input ref={fileInputRef} hidden accept=".csv,text/csv" type="file" onChange={handleCsvFileChange} />
            </Button>
            <Button disabled={!canPreview} variant="contained" onClick={handlePreview}>
              {isPreviewing ? 'Previewing...' : 'Preview'}
            </Button>
          </>
        )}
        {activeTab === 'preview' && (
          <>
            <Button
              disabled={sortedPreviewRows.length === 0}
              startIcon={<DownloadIcon />}
              variant="outlined"
              onClick={handlePreviewExport}
            >
              Export CSV
            </Button>
            <Button disabled={!canCommit} variant="contained" onClick={handleCommit}>
              {isCommitting
                ? 'Committing...'
                : `Commit ${selectedRows.length} row${selectedRows.length === 1 ? '' : 's'}`}
            </Button>
          </>
        )}
        {activeTab === 'commit' && (
          <>
            <Button disabled={commitRows.length === 0} variant="outlined" onClick={handleReverse}>
              Reverse
            </Button>
            <Button
              disabled={commitRows.length === 0}
              startIcon={<DownloadIcon />}
              variant="outlined"
              onClick={handleCommitExport}
            >
              Export CSV
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
